import React, { useState } from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, ShoppingCart, Check } from "lucide-react";
import { useCart } from "../context/CartContext";

const KIT_BADGE = {
  set: { label: "Set (boven + onder + bouten)", className: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  "set+stud": { label: "Set + stift", className: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  "set+thread": { label: "Set + draadstang", className: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  din3567: { label: "DIN 3567 set", className: "border-emerald-300 bg-emerald-50 text-emerald-800" },
  half: { label: "Halve beugel (1 stuk)", className: "border-amber-400 bg-amber-50 text-amber-900" },
  cap: { label: "Kapbeugel", className: "border-sky-300 bg-sky-50 text-sky-800" },
  other: { label: "—", className: "border-zinc-300 bg-zinc-50 text-zinc-700" },
};

export default function ProductCard({ p, dnContext, d1MmContext, inchContext }) {
  const kit = KIT_BADGE[p.kit_type] || KIT_BADGE.other;
  const { addItem } = useCart();
  const [added, setAdded] = useState(false);
  // Forward whichever context the catalog filter used so the detail page
  // pre-selects the variant the user was actually looking for.
  const params = new URLSearchParams();
  if (d1MmContext) params.set("d1_mm", d1MmContext);
  if (dnContext) params.set("dn", dnContext);
  if (inchContext) params.set("inch", inchContext);
  const qs = params.toString();
  const link = qs ? `/products/${p.id}?${qs}` : `/products/${p.id}`;

  // One-click "Voeg toe aan offerte" — handler used by the mini-spec
  // preview when finder-results are showing. Stops propagation so the
  // wrapping <Link> doesn't navigate to the detail page.
  const handleQuickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const mv = p.matched_variant;
    if (!mv || mv.price_on_request) return;
    addItem({
      product_id: p.id,
      sku: mv.sku,
      name: p.name_nl || p.name,
      image: p.image,
      material: mv.material || p.material,
      unit_price: Number(mv.price || 0),
      quantity: 1,
      meta: {
        dn: mv.dn,
        inch: mv.inch,
        diameter_mm: mv.diameter_mm,
        strip_size: mv.strip_size,
        box_qty: mv.box_qty,
        box_unit_price: mv.box_unit_price,
        piece_price: mv.price,
        box_discount_pct: p.box_discount_pct,
        pack_size: mv.pack_size || 0,
        subpack_surcharge: p.subpack_surcharge ?? 24.5,
      },
    });
    setAdded(true);
    window.setTimeout(() => setAdded(false), 1500);
  };
  const mats = (p.materials_available && p.materials_available.length > 0)
    ? p.materials_available
    : [p.material];
  const matLabel = (m) => (m === "galvanized" ? "GALVA" : (m || "").toUpperCase());
  return (
    <Link
      to={link}
      className="nibro-card group flex flex-col"
      data-testid={`product-card-${p.id}`}
    >
      <div className="nibro-card-header relative">
        <h3 className="font-display text-sm font-bold leading-tight line-clamp-2 min-h-[2.5rem] flex items-center justify-center gap-1.5">
          <span>{p.name_nl || p.name}</span>
          <span className="inline-flex flex-wrap items-center gap-1">
            {mats.map((m) => (
              <span
                key={m}
                className="inline-flex rounded-sm bg-white/25 px-1.5 py-0.5 font-mono text-[10px] font-bold tracking-wider text-white"
                data-testid={`product-card-material-${m}`}
              >
                {matLabel(m)}
              </span>
            ))}
          </span>
        </h3>
      </div>

      <div className="relative aspect-[4/3] overflow-hidden bg-white p-6">
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          className="h-full w-full object-contain transition duration-500 group-hover:scale-105"
        />
        {p.standard && (
          <span className="absolute left-3 top-3 rounded border border-nibro-sage bg-white/95 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-nibro-sage-dark">
            {p.standard}
          </span>
        )}
        {p.is_half && (
          <span
            className="absolute right-3 top-3 flex items-center gap-1 rounded border border-amber-400 bg-amber-50 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-wider text-amber-900"
            data-testid={`product-half-badge-${p.id}`}
          >
            <AlertTriangle size={10} /> 1/2
          </span>
        )}
      </div>

      {/* Kit type badge */}
      <div className={`mx-4 mb-2 mt-3 rounded border px-2 py-1 text-center font-mono text-[10px] font-bold uppercase tracking-wider ${kit.className}`}
           data-testid={`product-kit-badge-${p.id}`}>
        {kit.label}
      </div>

      <div className="border-t border-zinc-100 px-4 py-3">
        <div className="flex items-center justify-between text-xs">
          <span className="font-mono text-zinc-500">
            {mats.map(matLabel).join(" · ")} · {p.variant_count}× maten
          </span>
          <span className="font-display text-base font-bold text-zinc-900">
            v.a. €{p.price_from.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Mini-spec preview — only rendered when the catalog page is
          showing finder-results (technical filter) and the backend
          returned the matching variant. Lets the buyer confirm the
          requested size is in stock without drilling in. */}
      {p.matched_variant && (
        <div
          className="border-t-2 border-nibro-sage/40 bg-nibro-sage-pale px-4 py-3"
          data-testid={`product-card-matched-${p.id}`}
        >
          <div className="font-mono text-[10px] font-bold uppercase tracking-widest text-nibro-sage-dark">
            Voor jouw maat
          </div>
          <div className="mt-1.5 flex items-baseline justify-between gap-2">
            <div className="font-display text-sm font-bold text-zinc-900 leading-snug">
              Ø {p.matched_variant.diameter_mm} mm
              {p.matched_variant.dn ? ` · DN${p.matched_variant.dn}` : ""}
              {p.matched_variant.inch ? ` · ${p.matched_variant.inch}` : ""}
            </div>
            {p.matched_variant.price_on_request ? (
              <span className="rounded-full border border-amber-300 bg-amber-50 px-2 py-0.5 font-mono text-[10px] font-bold text-amber-800">
                Op aanvraag
              </span>
            ) : (
              <span className="font-display text-base font-bold text-emerald-700">
                €{Number(p.matched_variant.price || 0).toFixed(2)}<span className="font-mono text-[10px] font-normal text-zinc-500">/stuk</span>
              </span>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 font-mono text-[10px] text-zinc-600">
            <span>SKU {p.matched_variant.sku}</span>
            {p.matched_variant.strip_size && <span>· {p.matched_variant.strip_size}</span>}
            {p.matched_variant.bolts_nuts && p.matched_variant.bolts_nuts !== "-" && (
              <span>· {p.matched_variant.bolts_nuts}</span>
            )}
            {p.matched_variant.box_qty > 0 && !p.matched_variant.price_on_request && (
              <span className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 text-[10px] font-bold text-nibro-sage-dark">
                Omdoos {p.matched_variant.box_qty}× · €{Number(p.matched_variant.box_unit_price || 0).toFixed(2)}/stuk
              </span>
            )}
          </div>

          {/* One-click add to quote (B2B speed-flow). For price-on-request
              variants we render a "Vraag offerte aan" pill that links to
              the contact form pre-filled with the SKU. */}
          {p.matched_variant.price_on_request ? (
            <Link
              to={`/contact?sku=${encodeURIComponent(p.matched_variant.sku)}&subject=${encodeURIComponent("Offerteaanvraag " + p.matched_variant.sku)}`}
              onClick={(e) => e.stopPropagation()}
              data-testid={`product-card-request-quote-${p.id}`}
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-md bg-amber-500 px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-amber-600"
            >
              <AlertTriangle size={12} /> Vraag offerte aan
            </Link>
          ) : (
            <button
              type="button"
              onClick={handleQuickAdd}
              data-testid={`product-card-quick-add-${p.id}`}
              className={`mt-3 flex w-full items-center justify-center gap-2 rounded-md px-3 py-2 text-xs font-bold uppercase tracking-wide text-white transition ${
                added ? "bg-emerald-500" : "bg-nibro-sage hover:bg-nibro-sage-hover"
              }`}
            >
              {added ? (
                <><Check size={12} /> Toegevoegd</>
              ) : (
                <><ShoppingCart size={12} /> Direct bestellen</>
              )}
            </button>
          )}
        </div>
      )}
    </Link>
  );
}
